import { defineStore } from 'pinia'
import { NotificationSchema } from '~/schemas/notification'
import type { Notification } from '~/schemas/notification'
import type { PaginationMeta } from '~/types/pagination'

type NotificationsState = {
  items: Notification[]
  meta: PaginationMeta | null
  loading: boolean
  error: string | null
  unreadCount: number
  pollingInterval: ReturnType<typeof setInterval> | null
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    items: [],
    meta: null,
    loading: false,
    error: null,
    unreadCount: 0,
    pollingInterval: null,
  }),

  getters: {
    totalItems: (state) => state.meta?.total || 0,
    currentPage: (state) => state.meta?.current_page || 1,
    lastPage: (state) => state.meta?.last_page || 1,
  },

  actions: {
    async fetchNotifications(url: string = '/api/notifications') {
      this.loading = true
      this.error = null

      try {
        const { fetchPaginated } = useApiResponse()
        const response = await fetchPaginated<Notification>(url, NotificationSchema)

        this.items = response.data
        this.meta = response.meta

        return response.data
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch notifications'
        console.error('Failed to fetch notifications:', error)
        return null
      } finally {
        this.loading = false
      }
    },

    async fetchUnreadCount() {
      try {
        const sanctumClient = useSanctumClient()
        const response = await sanctumClient('/api/notifications/unread-count') as { unread_count: number }
        this.unreadCount = response.unread_count
      } catch (error) {
        console.error('Failed to fetch unread count:', error)
      }
    },

    async markAsRead(id: string) {
      const item = this.items.find(n => n.id === id)
      if (item && item.read_at) return

      // Optimistic update
      if (item) {
        item.read_at = new Date().toISOString()
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }

      try {
        const sanctumClient = useSanctumClient()
        await sanctumClient(`/api/notifications/${id}/read`, { method: 'PATCH' })
      } catch (error) {
        // Revert optimistic update
        if (item) {
          item.read_at = null
          this.unreadCount += 1
        }
        console.error('Failed to mark as read:', error)
      }
    },

    async markAsUnread(id: string) {
      const item = this.items.find(n => n.id === id)
      if (item && !item.read_at) return

      // Optimistic update
      const previousReadAt = item?.read_at
      if (item) {
        item.read_at = null
        this.unreadCount += 1
      }

      try {
        const sanctumClient = useSanctumClient()
        await sanctumClient(`/api/notifications/${id}/unread`, { method: 'PATCH' })
      } catch (error) {
        // Revert optimistic update
        if (item) {
          item.read_at = previousReadAt ?? new Date().toISOString()
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
        console.error('Failed to mark as unread:', error)
      }
    },

    async markAllAsRead() {
      const previousItems = this.items.map(n => ({ ...n }))
      const previousCount = this.unreadCount

      // Optimistic update
      this.items.forEach((n) => {
        if (!n.read_at) {
          n.read_at = new Date().toISOString()
        }
      })
      this.unreadCount = 0

      try {
        const sanctumClient = useSanctumClient()
        await sanctumClient('/api/notifications/read-all', { method: 'PATCH' })
      } catch (error) {
        // Revert optimistic update
        this.items = previousItems
        this.unreadCount = previousCount
        console.error('Failed to mark all as read:', error)
      }
    },

    async deleteNotification(id: string) {
      const index = this.items.findIndex(n => n.id === id)
      const item = index !== -1 ? this.items[index] : null

      // Optimistic update
      if (index !== -1) {
        this.items.splice(index, 1)
        if (this.meta) this.meta.total -= 1
        if (item && !item.read_at) {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
      }

      try {
        const sanctumClient = useSanctumClient()
        await sanctumClient(`/api/notifications/${id}`, { method: 'DELETE' })
      } catch (error) {
        // Revert optimistic update
        if (item && index !== -1) {
          this.items.splice(index, 0, item)
          if (this.meta) this.meta.total += 1
          if (!item.read_at) {
            this.unreadCount += 1
          }
        }
        console.error('Failed to delete notification:', error)
      }
    },

    startPolling(intervalMs: number = 30000) {
      this.stopPolling()
      this.fetchUnreadCount()
      this.pollingInterval = setInterval(() => {
        this.fetchUnreadCount()
      }, intervalMs)
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
  },
})
